import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Car, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { setRole, user } = useAuth();
  const navigate = useNavigate();
  const [pick, setPick] = useState("owner");
  const [shopName, setShopName] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) {
    navigate("/login");
    return null;
  }

  const submit = async () => {
    if (pick === "shop" && !shopName.trim()) {
      toast.error("Shop name is required");
      return;
    }
    setLoading(true);
    try {
      const u = await setRole({
        role: pick,
        shop_name: pick === "shop" ? shopName : null,
        bio: pick === "shop" ? bio : null,
      });
      navigate(u.role === "shop" ? "/shop" : "/owner");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6 safe-top safe-bottom">
      <div className="w-full max-w-2xl border border-white/10 bg-[#121214] p-8 sm:p-12">
        <div className="label-eyebrow mb-4">ONE MORE STEP</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
          Are you fixing or getting fixed?
        </h1>
        <p className="text-zinc-400 mb-10">Choose your role. This unlocks the right dashboard.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button
            type="button"
            onClick={() => setPick("owner")}
            className={`p-6 border text-left transition-all ${pick === "owner" ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"}`}
            data-testid="pick-owner"
          >
            <Car className="w-7 h-7 mb-4 text-[#FF3B30]" strokeWidth={1.5} />
            <div className="font-heading text-lg font-bold mb-1">CAR OWNER</div>
            <div className="text-sm text-zinc-500">Post issues, collect anonymous quotes, choose your mechanic.</div>
          </button>
          <button
            type="button"
            onClick={() => setPick("shop")}
            className={`p-6 border text-left transition-all ${pick === "shop" ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"}`}
            data-testid="pick-shop"
          >
            <Wrench className="w-7 h-7 mb-4 text-[#FF3B30]" strokeWidth={1.5} />
            <div className="font-heading text-lg font-bold mb-1">REPAIR SHOP</div>
            <div className="text-sm text-zinc-500">Browse open jobs, send private quotes, earn 5-star ratings.</div>
          </button>
        </div>

        {pick === "shop" && (
          <div className="space-y-4 mb-8">
            <div>
              <label className="label-eyebrow block mb-2">SHOP NAME</label>
              <input value={shopName} onChange={(e) => setShopName(e.target.value)} className="input-base" data-testid="onb-shop-name" placeholder="Main St Auto" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">SHORT BIO</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className="input-base" data-testid="onb-bio" placeholder="What you specialize in..." />
            </div>
          </div>
        )}

        <button onClick={submit} disabled={loading} className="btn-primary w-full" data-testid="onb-continue">
          {loading ? "..." : "Continue"}
        </button>
      </div>
    </div>
  );
}
